import { ButtonPro } from 'components';
import LogoSVG from 'components/atoms/LogoSVG';
import {
  getMeaningfulApprovals,
  getApprovalStatusClassName,
  getApprovalStatusLabel
} from 'utils/leaveApprovals';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store';
import { getEmployees } from 'store/MasterData';
import {
  LeaveApproval,
  LeaveRequestItem,
  LeaveTypeResponse,
  getAssignedLeaveRequests,
  getLeaveTypes
} from 'store/Leaves';
import './ManagerLeaveRequests.scss';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Requests' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'Approved' },
  { value: '2', label: 'Rejected' },
  { value: '3', label: 'Cancellation Pending' },
  { value: '4', label: 'Cancelled' }
];

const ManagerLeaveRequests = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasFetchStarted = useRef(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    leaveTypeInfo,
    allLeaveRequests,
    allLeaveRequestsLoading,
    allLeaveRequestsError,
    allLeaveRequestsTotalPages,
    allLeaveRequestsTotalCount
  } = useAppSelector((state) => state.leaves);
  const employees = useAppSelector((state) => state.masterData.employees);

  useEffect(() => {
    dispatch(getLeaveTypes());
    dispatch(getEmployees());
  }, [dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPageNumber(1);
    }, searchTerm.trim() ? 500 : 0);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(
      getAssignedLeaveRequests({
        pageNumber,
        pageSize,
        statusFilter: statusFilter || 'all',
        search: debouncedSearch || undefined
      })
    );
  }, [dispatch, pageNumber, pageSize, statusFilter, debouncedSearch]);

  useEffect(() => {
    if (allLeaveRequestsLoading) {
      hasFetchStarted.current = true;
      return;
    }
    if (hasFetchStarted.current) {
      setHasLoadedOnce(true);
    }
  }, [allLeaveRequestsLoading]);

  const fetchLeaveRequests = () => {
    dispatch(
      getAssignedLeaveRequests({
        pageNumber,
        pageSize,
        statusFilter,
        search: debouncedSearch || undefined
      })
    );
  };

  const handleLeaveCardClick = (leave: LeaveRequestItem) => {
    navigate(
      `/respondLeaveRequest?leaveId=${leave.leaveDetail.detailsId}&updated=${leave.updatedAsTicks}`
    );
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPageNumber(1);
  };

  function findLeaveType(leaveTypeId: string) {
    return leaveTypeInfo?.find(
      (leaveType: LeaveTypeResponse) => leaveType.leaveTypeID === leaveTypeId
    );
  }

  function findEmpName(id: string) {
    if (!employees || employees.length === 0) return '';
    const employee = employees.find((emp) => emp.employeeId === id);
    return employee ? employee.employeeName : '';
  }

  return (
    <div className="manager-leave-requests">
      <div className="manager-leave-requests__toolbar">
        <h2 className="manager-leave-requests__title">
          All Leave Requests ({allLeaveRequestsTotalCount})
        </h2>

        <div className="manager-leave-requests__filters">
          <div className="manager-leave-requests__field">
            <label htmlFor="manager-leave-employee-search">Employee name</label>
            <input
              id="manager-leave-employee-search"
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="manager-leave-requests__field">
            <label htmlFor="manager-leave-status-filter">Status</label>
            <select
              id="manager-leave-status-filter"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {allLeaveRequestsLoading && !hasLoadedOnce ? (
        <div className="manager-leave-requests__loading">
          <LogoSVG />
        </div>
      ) : allLeaveRequestsError ? (
        <div className="manager-leave-requests__error">
          <strong>Error:</strong> {allLeaveRequestsError}
          <div style={{ marginTop: 12 }}>
            <ButtonPro
              label="Refresh"
              type="primary"
              htmlType="button"
              onClick={fetchLeaveRequests}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="manager-leave-requests__list">
            {allLeaveRequests.length === 0 ? (
              <div className="manager-leave-requests__empty">
                No leave requests match the current filters.
              </div>
            ) : (
              allLeaveRequests.map((leave: LeaveRequestItem) => {
                const meaningfulApprovals = getMeaningfulApprovals(
                  leave.leaveApprovals
                );

                return (
                  <div
                    className="manager-leave-card"
                    key={leave.leaveDetail.detailsId}
                  >
                    <div className="manager-leave-card__header">
                      <div className="manager-leave-card__info">
                        <h3>{leave.requesterNickName}</h3>
                        <span className="manager-leave-card__type">
                          {findLeaveType(leave.leaveDetail.leaveTypeId)
                            ?.leaveType || 'Leave Type'}
                        </span>
                      </div>
                      <span
                        className={`manager-leave-card__status status-${leave.leaveDetail.leaveStatus}`}
                      >
                        {leave.leaveDetail.leaveStatus === 0
                          ? 'Pending'
                          : leave.leaveDetail.leaveStatus === 1
                            ? 'Approved'
                            : leave.leaveDetail.leaveStatus === 3
                              ? 'Cancellation Pending'
                              : leave.leaveDetail.leaveStatus === 4
                                ? 'Cancelled'
                                : 'Rejected'}
                      </span>
                    </div>

                    <div className="manager-leave-card__body">
                      <div className="manager-leave-card__grid">
                        <div>
                          <span className="label">Employee UPN</span>
                          <span className="value">{leave.leaveDetail.upn}</span>
                        </div>
                        <div>
                          <span className="label">Start date</span>
                          <span className="value">
                            {leave.leaveDetail.startDate.slice(0, 10)}
                          </span>
                        </div>
                        <div>
                          <span className="label">End date</span>
                          <span className="value">
                            {leave.leaveDetail.endDate.slice(0, 10)}
                          </span>
                        </div>
                        <div>
                          <span className="label">Days</span>
                          <span className="value">
                            {leave.leaveDetail.noOfDays}
                          </span>
                        </div>
                        <div>
                          <span className="label">Session</span>
                          <span className="value">
                            {leave.leaveDetail.leaveSession === 0
                              ? 'Morning'
                              : leave.leaveDetail.leaveSession === 1
                                ? 'Evening'
                                : 'Full Day'}
                          </span>
                        </div>
                        <div>
                          <span className="label">Requested date</span>
                          <span className="value">
                            {new Date(
                              leave.leaveDetail.requestedDate
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="manager-leave-card__section">
                        <span className="label">Reason</span>
                        <p>{leave.leaveDetail.reason}</p>
                      </div>

                      {meaningfulApprovals.length > 0 ? (
                        <div className="manager-leave-card__section">
                          <span className="label">Approvals</span>
                          <div className="manager-leave-card__approvals">
                            {meaningfulApprovals.map(
                              (approval: LeaveApproval) => (
                                <div key={approval.id} className="approval-row">
                                  <span
                                    className={getApprovalStatusClassName(
                                      approval
                                    )}
                                  >
                                    {getApprovalStatusLabel(approval)}
                                  </span>
                                  <span className="approval-approver">
                                    by {findEmpName(approval.approverId)}
                                  </span>
                                  <span className="approval-date">
                                    {new Date(
                                      approval.approvalDate
                                    ).toLocaleDateString()}
                                  </span>
                                  {approval.comments && (
                                    <span className="approval-comment">
                                      &ldquo;{approval.comments}&rdquo;
                                    </span>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="manager-leave-card__section muted">
                          <em>No approval decisions yet</em>
                        </div>
                      )}

                      <div className="manager-leave-card__actions">
                        <ButtonPro
                          label="Review & Respond"
                          type="white"
                          htmlType="button"
                          onClick={() => handleLeaveCardClick(leave)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {allLeaveRequestsTotalPages > 1 && (
            <div className="manager-leave-requests__pagination">
              <ButtonPro
                label="Previous"
                type="white"
                htmlType="button"
                onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                disabled={pageNumber === 1}
              />
              <div className="pagination-info">
                <span>
                  Page {pageNumber} of {allLeaveRequestsTotalPages}
                </span>
                <span className="total-count">
                  {allLeaveRequestsTotalCount} total
                </span>
              </div>
              <ButtonPro
                label="Next"
                type="white"
                htmlType="button"
                onClick={() =>
                  setPageNumber((prev) =>
                    Math.min(allLeaveRequestsTotalPages, prev + 1)
                  )
                }
                disabled={pageNumber === allLeaveRequestsTotalPages}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManagerLeaveRequests;
